import React, { useState, useRef } from 'react';

interface DateInputProps {
  id?: string;
  value: string; // YYYY-MM-DD
  onChange: (value: string) => void;
  disabled?: boolean;
  min?: string; // YYYY-MM-DD
  style?: React.CSSProperties;
}

/**
 * 직접 타이핑(YYYYMMDD 또는 YYYY-MM-DD) + 달력 아이콘 클릭 모두 지원하는 날짜 입력 컴포넌트
 */
const DateInput: React.FC<DateInputProps> = ({ id, value, onChange, disabled, min, style }) => {
  const [textValue, setTextValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const hiddenDateRef = useRef<HTMLInputElement>(null);

  // value prop이 외부에서 변경되면 textValue도 동기화
  React.useEffect(() => {
    if (!isFocused) {
      setTextValue(value);
    }
  }, [value, isFocused]);

  const parseAndApply = (raw: string) => {
    // YYYYMMDD -> YYYY-MM-DD
    const digits = raw.replace(/[^0-9]/g, '');
    if (digits.length === 8) {
      const formatted = `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
      const d = new Date(formatted);
      if (!isNaN(d.getTime()) && formatted === d.toISOString().slice(0, 10)) {
        onChange(formatted);
        setTextValue(formatted);
        return;
      }
    }
    // YYYY-MM-DD 그대로
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const d = new Date(raw);
      if (!isNaN(d.getTime()) && raw === d.toISOString().slice(0, 10)) {
        onChange(raw);
        setTextValue(raw);
        return;
      }
    }
    // 유효하지 않거나 min 이전이면 원래 값으로 복원
    setTextValue(value);
  };

  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <input
        id={id}
        type="text"
        value={isFocused ? textValue : value}
        disabled={disabled}
        placeholder="YYYY-MM-DD"
        onChange={(e) => setTextValue(e.target.value)}
        onFocus={() => {
          setIsFocused(true);
          setTextValue(value);
        }}
        onBlur={() => {
          setIsFocused(false);
          parseAndApply(textValue);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            parseAndApply(textValue);
            (e.target as HTMLInputElement).blur();
          }
        }}
        style={{
          padding: '6px 10px',
          fontSize: '12px',
          border: '1px solid #93c5fd',
          borderRadius: '4px',
          outline: 'none',
          paddingRight: '28px',
          width: '130px',
          ...style,
        }}
      />
      {/* 달력 아이콘 버튼 */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => hiddenDateRef.current?.showPicker()}
        style={{
          position: 'absolute',
          right: '4px',
          background: 'none',
          border: 'none',
          cursor: disabled ? 'default' : 'pointer',
          padding: '2px',
          display: 'flex',
          alignItems: 'center',
          opacity: disabled ? 0.5 : 0.6,
        }}
        tabIndex={-1}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="1" y="3" width="14" height="12" rx="1.5" stroke="#1e40af" strokeWidth="1.2" />
          <line x1="1" y1="7" x2="15" y2="7" stroke="#1e40af" strokeWidth="1.2" />
          <line x1="4.5" y1="1.5" x2="4.5" y2="4.5" stroke="#1e40af" strokeWidth="1.2" strokeLinecap="round" />
          <line x1="11.5" y1="1.5" x2="11.5" y2="4.5" stroke="#1e40af" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      </button>
      {/* 숨겨진 date input (달력 picker용) */}
      <input
        ref={hiddenDateRef}
        type="date"
        value={value}
        min={min}
        onChange={(e) => {
          onChange(e.target.value);
          setTextValue(e.target.value);
        }}
        style={{
          position: 'absolute',
          opacity: 0,
          width: 0,
          height: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
        tabIndex={-1}
      />
    </div>
  );
};

export default DateInput;
